import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import type { ResponsiveLayouts, LayoutItem, Compactor, Layout, EventCallback } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { Plus, Check, X, RotateCcw } from 'lucide-react';
import { useStockDashboardStore } from '../../store/stockDashboardStore';
import { useProjectStore } from '../../store/projectStore';
import { useWarehouseStore } from '../../store/warehouseStore';
import WidgetShell from './widgets/WidgetShell';
import KpiWidget from './widgets/KpiWidget';
import CategoryWidget from './widgets/CategoryWidget';
import AlertsWidget from './widgets/AlertsWidget';
import RecentActivityWidget from './widgets/RecentActivityWidget';
import StockByItemWidget from './widgets/StockByItemWidget';
import WidgetConfigPanel from './WidgetConfigPanel';
import AddWidgetPicker from './AddWidgetPicker';
import type { WidgetConfig } from '../../store/stockDashboardStore';

// ── Multi-displace compactor ──────────────────────────────────────────────────
// When widget A is dragged:
//   • Every widget whose *origin* overlaps A's current position is displaced.
//   • Each displaced widget uses a BFS to find its nearest free grid cell
//     (closest Manhattan distance from its own origin, outside A's footprint).
//   • Non-displaced widgets are pinned to their pre-drag origins — no drift.
//   • compact() is a no-op when no drag is active, so gaps are preserved.
//
// On drop, handleDragStop runs the same resolver once more to guarantee the
// final saved layout has zero overlaps.
// ─────────────────────────────────────────────────────────────────────────────
type OriginMap = Record<string, { x: number; y: number; w: number; h: number }>;

const _drag: { activeId: string | null; origins: OriginMap } = {
  activeId: null,
  origins: {},
};

function itemsCollide(a: LayoutItem, b: LayoutItem): boolean {
  if (a.i === b.i) return false;
  return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
}

// BFS outward from `item`'s pre-drag origin to find the nearest grid cell
// not occupied by any item in `placed`.  Returns a new LayoutItem at that slot.
function findFreeSlot(item: LayoutItem, placed: LayoutItem[], cols: number): LayoutItem {
  const orig = _drag.origins[item.i] ?? item;
  const visited = new Set<string>();
  const queue: Array<{ x: number; y: number }> = [{ x: orig.x, y: orig.y }];

  while (queue.length > 0 && visited.size < 800) {
    const { x, y } = queue.shift()!;
    const key = `${x},${y}`;
    if (visited.has(key)) continue;
    visited.add(key);

    if (x < 0 || y < 0 || x + item.w > cols) continue;

    const candidate = { ...item, x, y };
    if (!placed.some(p => itemsCollide(candidate, p))) return candidate;

    // Expand neighbours – right/left first to prefer same-row placements
    if (x + 1 + item.w <= cols) queue.push({ x: x + 1, y });
    if (x - 1 >= 0)             queue.push({ x: x - 1, y });
    queue.push({ x, y: y + 1 });
    if (y - 1 >= 0)             queue.push({ x, y: y - 1 });
  }

  // Fallback: park below everything
  const maxY = placed.reduce((m, p) => Math.max(m, p.y + p.h), 0);
  return { ...item, x: 0, y: maxY };
}

// Resolve all overlaps in a flat layout array.
// Used both by the live compactor and by handleDragStop for the final commit.
function resolveLayout(
  anchor: LayoutItem,          // the "fixed" widget (dragged or just-dropped)
  rest: LayoutItem[],
  cols: number,
): LayoutItem[] {
  const placed: LayoutItem[] = [{ ...anchor }];

  // Separate non-displaced (go to origin) from displaced (need a free slot)
  const toDisplace: LayoutItem[] = [];
  for (const item of rest) {
    const orig = _drag.origins[item.i];
    const atOrigin: LayoutItem = orig ? { ...item, x: orig.x, y: orig.y } : { ...item };
    if (itemsCollide(atOrigin, anchor)) {
      toDisplace.push(item);
    } else {
      placed.push(atOrigin);
    }
  }

  // Process displaced items in sorted order (top-left first) so earlier
  // items get the slots closest to their origin.
  const sorted = toDisplace.slice().sort((a, b) => {
    const oa = _drag.origins[a.i] ?? a;
    const ob = _drag.origins[b.i] ?? b;
    return oa.y !== ob.y ? oa.y - ob.y : oa.x - ob.x;
  });

  for (const item of sorted) {
    placed.push(findFreeSlot(item, placed, cols));
  }

  return placed;
}

const dashboardCompactor: Compactor = {
  type: null,
  allowOverlap: false,
  preventCollision: false,

  compact(layout: Layout, cols: number): Layout {
    if (!_drag.activeId) return [...layout];

    const dragged = layout.find(it => it.i === _drag.activeId);
    if (!dragged) return [...layout];

    const rest = (layout as LayoutItem[]).filter(it => it.i !== _drag.activeId);
    const placed = resolveLayout(dragged, rest, cols);

    return (layout as LayoutItem[]).map(orig => placed.find(p => p.i === orig.i) ?? orig);
  },
};

// ── Grid overlay: renders matching column/row ghost cells in edit mode ──
const GRID_MARGIN     = 10;
const GRID_PADDING    = 12;
const GRID_ROW_HEIGHT = 70;
const GRID_BREAKPOINTS = [
  { minW: 1200, cols: 12 },
  { minW: 996,  cols: 8  },
  { minW: 768,  cols: 4  },
  { minW: 480,  cols: 2  },
  { minW: 0,    cols: 1  },
];

function getColsForWidth(w: number) {
  return (GRID_BREAKPOINTS.find(bp => w >= bp.minW) ?? GRID_BREAKPOINTS[GRID_BREAKPOINTS.length - 1]).cols;
}

const GRID_BUFFER_ROWS = 8; // extra empty rows to draw below the lowest widget

interface GridOverlayProps {
  width: number;
  isDark: boolean;
  layouts: import('react-grid-layout').ResponsiveLayouts;
}

const GridOverlay: React.FC<GridOverlayProps> = ({ width, isDark, layouts }) => {
  const cols     = getColsForWidth(width);
  const colWidth = (width - GRID_PADDING * 2 - GRID_MARGIN * (cols - 1)) / cols;

  // Work out which breakpoint key best matches the current width
  const bpKey = width >= 1200 ? 'lg' : width >= 996 ? 'md' : width >= 768 ? 'sm' : width >= 480 ? 'xs' : 'xxs';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bpLayout: { y: number; h: number }[] = ((layouts as unknown as Record<string, { y: number; h: number }[]>)[bpKey]) ?? [];

  // Rows needed = lowest widget bottom + buffer, minimum 12
  const maxRow = bpLayout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
  const numRows = Math.max(12, maxRow + GRID_BUFFER_ROWS);

  // Total pixel height the overlay must cover
  const totalHeight = GRID_PADDING + numRows * (GRID_ROW_HEIGHT + GRID_MARGIN);

  const cellBg   = isDark ? 'rgba(99,149,255,0.03)'  : 'rgba(99,149,255,0.035)';
  const cellLine = isDark ? 'rgba(99,149,255,0.11)'  : 'rgba(99,149,255,0.17)';

  return (
    <div
      className="absolute top-0 left-0 right-0 pointer-events-none"
      style={{ height: totalHeight, zIndex: 0 }}
    >
      {Array.from({ length: cols }, (_, ci) =>
        Array.from({ length: numRows }, (_, ri) => (
          <div
            key={`${ci}-${ri}`}
            className="absolute"
            style={{
              left:            GRID_PADDING + ci * (colWidth + GRID_MARGIN),
              top:             GRID_PADDING + ri * (GRID_ROW_HEIGHT + GRID_MARGIN),
              width:           colWidth,
              height:          GRID_ROW_HEIGHT,
              backgroundColor: cellBg,
              border:          `1px dashed ${cellLine}`,
              borderRadius:    3,
              boxSizing:       'border-box',
            }}
          />
        ))
      )}
    </div>
  );
};

const StockDashboard: React.FC = () => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';

  const {
    widgets,
    layouts,
    isEditMode,
    activeConfigWidgetId,
    enterEditMode,
    exitEditMode,
    updateLayouts,
    addWidget,
    removeWidget,
    updateWidgetConfig,
    scopeMode,
    globalScopeWarehouseId,
    globalScopeStorageAreaId,
    setActiveConfigWidget,
    resetToDefaults,
  } = useStockDashboardStore();
  const { warehouses, storageAreasByWarehouse } = useWarehouseStore();

  const [showAddPicker, setShowAddPicker] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const activeConfigWidget = widgets.find(w => w.id === activeConfigWidgetId) ?? null;

  // v2 API: useContainerWidth replaces WidthProvider HOC.
  // We keep a "last known good width" so the grid never collapses to 0 when
  // the container is hidden via display:none (while switching main nav tabs).
  const { width: measuredWidth, containerRef, mounted } = useContainerWidth({ initialWidth: 1200 });
  const lastGoodWidthRef = useRef(1200);
  useEffect(() => {
    if (measuredWidth > 50) lastGoodWidthRef.current = measuredWidth;
  }, [measuredWidth]);
  const width = measuredWidth > 50 ? measuredWidth : lastGoodWidthRef.current;

  const handleLayoutChange = useCallback(
    (_: unknown, allLayouts: ResponsiveLayouts) => {
      if (isEditMode) updateLayouts(allLayouts);
    },
    [isEditMode, updateLayouts],
  );

  // Snapshot pre-drag origins so the compactor can pin / displace correctly.
  const handleDragStart: EventCallback = useCallback((currentLayout, oldItem) => {
    if (!oldItem) return;
    _drag.activeId = oldItem.i;
    _drag.origins  = {};
    (currentLayout as LayoutItem[]).forEach(item => {
      _drag.origins[item.i] = { x: item.x, y: item.y, w: item.w, h: item.h };
    });
  }, []);

  // On drop: keep drag state alive one rAF so the final compact() run (which
  // fires before onLayoutChange) still resolves overlaps, then clear.
  const handleDragStop: EventCallback = useCallback(() => {
    requestAnimationFrame(() => {
      _drag.activeId = null;
      _drag.origins  = {};
    });
  }, []);

  const warehouseById = useCallback(
    (id: string | null | undefined) => warehouses.find(w => w.id === id) ?? null,
    [warehouses],
  );
  const areaById = useCallback(
    (warehouseId: string | null | undefined, areaId: string | null | undefined) => {
      if (!warehouseId || !areaId) return null;
      const areas = storageAreasByWarehouse[warehouseId] ?? [];
      return areas.find(a => a.id === areaId) ?? null;
    },
    [storageAreasByWarehouse],
  );

  const resolveDataScope = useCallback(
    (widget: WidgetConfig): { warehouseId: string | null; storageAreaId: string | null } => {
      if (scopeMode === 'global' || widget.scopeType === 'inherit' || !widget.scopeType) {
        return { warehouseId: globalScopeWarehouseId, storageAreaId: globalScopeStorageAreaId };
      }
      if (widget.scopeType === 'storage_area') {
        return {
          warehouseId: widget.scopeWarehouseId ?? null,
          storageAreaId: widget.scopeStorageAreaId ?? null,
        };
      }
      return { warehouseId: widget.scopeWarehouseId ?? null, storageAreaId: null };
    },
    [scopeMode, globalScopeWarehouseId, globalScopeStorageAreaId],
  );

  const renderWidgetContent = (widget: WidgetConfig) => {
    const common = {
      title: widget.title,
      accentColor: widget.accentColor,
      bgTint: widget.bgTint,
      isDark,
    };
    switch (widget.type) {
      case 'kpi':      return <KpiWidget      {...common} kpiMetric={widget.kpiMetric} />;
      case 'category': return <CategoryWidget {...common} />;
      case 'alerts':   return <AlertsWidget   {...common} />;
      case 'activity': return <RecentActivityWidget {...common} />;
      case 'stock_by_item': {
        const s = resolveDataScope(widget);
        return (
          <StockByItemWidget
            {...common}
            catalogueItemId={widget.stockCatalogueItemId ?? null}
            scopeWarehouseId={s.warehouseId}
            scopeStorageAreaId={s.storageAreaId}
          />
        );
      }
      case 'stock_by_item_counts': {
        const s = resolveDataScope(widget);
        return (
          <StockByItemWidget
            {...common}
            catalogueItemId={widget.stockCatalogueItemId ?? null}
            scopeWarehouseId={s.warehouseId}
            scopeStorageAreaId={s.storageAreaId}
            variant="condition_counts"
          />
        );
      }
      default:         return null;
    }
  };

  const getWidgetScopeBadge = useCallback((widget: WidgetConfig) => {
    const globalWarehouse = warehouseById(globalScopeWarehouseId);
    const globalArea = areaById(globalScopeWarehouseId, globalScopeStorageAreaId);

    const inherited = {
      type: (globalArea ? 'storage_area' : 'warehouse') as 'storage_area' | 'warehouse',
      label: globalArea
        ? `${globalWarehouse?.name ?? 'Warehouse'} / ${globalArea.name}`
        : (globalWarehouse?.name ?? 'All Warehouses'),
      isCustom: false,
    };

    if (scopeMode === 'global' || widget.scopeType === 'inherit' || !widget.scopeType) {
      return inherited;
    }

    if (widget.scopeType === 'storage_area') {
      const wh = warehouseById(widget.scopeWarehouseId);
      const ar = areaById(widget.scopeWarehouseId, widget.scopeStorageAreaId);
      return {
        type: 'storage_area' as const,
        label: ar ? `${wh?.name ?? 'Warehouse'} / ${ar.name}` : inherited.label,
        isCustom: true,
      };
    }

    const wh = warehouseById(widget.scopeWarehouseId);
    return {
      type: 'warehouse' as const,
      label: wh?.name ?? inherited.label,
      isCustom: true,
    };
  }, [
    areaById,
    globalScopeStorageAreaId,
    globalScopeWarehouseId,
    scopeMode,
    warehouseById,
  ]);

  // v2 API: DragConfig uses `enabled`, ResizeConfig uses `enabled` and `handles`
  const dragCfg = { enabled: isEditMode };
  const resizeCfg = {
    enabled: isEditMode,
    handles: ['se', 'sw', 'ne', 'nw', 'e', 'w', 's', 'n'] as ('s' | 'w' | 'e' | 'n' | 'sw' | 'nw' | 'se' | 'ne')[],
  };

  return (
    <div className={`relative flex flex-col flex-1 min-h-0 overflow-hidden ${isDark ? 'bg-[#171a1f]' : 'bg-gray-100'}`}>

      {/* ── Edit-mode top toolbar ── */}
      {isEditMode && (
        <div
          className="flex-shrink-0 flex items-center justify-between px-4 py-2"
          style={{
            borderBottom: `1px solid ${isDark ? '#343a43' : '#e5e7eb'}`,
            backgroundColor: isDark ? '#20242b' : '#ffffff',
          }}
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse inline-block" />
            <span className={`text-xs font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
              Edit Mode — drag &amp; resize widgets, or click ⚙ to configure
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowResetConfirm(true)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                isDark
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-[#2b3140]'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <RotateCcw size={12} />
              Reset
            </button>
            <button
              onClick={() => { setShowAddPicker(false); exitEditMode(false); }}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                isDark
                  ? 'text-gray-300 hover:bg-[#2b3140]'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <X size={12} />
              Cancel
            </button>
            <button
              onClick={() => { setShowAddPicker(false); exitEditMode(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            >
              <Check size={12} />
              Done
            </button>
          </div>
        </div>
      )}

      {/* ── Scrollable grid area ── */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <div className={`flex-1 overflow-auto relative${isEditMode ? ' select-none' : ''}`} ref={containerRef as any}>

        {/* Grid overlay — only visible in edit mode, extends past all widgets */}
        {isEditMode && mounted && <GridOverlay width={width} isDark={isDark} layouts={layouts} />}

        {mounted && (
          <ResponsiveGridLayout
            className="stock-grid"
            width={width}
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 8, sm: 4, xs: 2, xxs: 1 }}
            rowHeight={70}
            margin={[10, 10]}
            containerPadding={[12, 12]}
            dragConfig={dragCfg}
            resizeConfig={resizeCfg}
            compactor={dashboardCompactor}
            onLayoutChange={handleLayoutChange}
            onDragStart={handleDragStart}
            onDragStop={handleDragStop}
          >
            {widgets.map(widget => (
              <div key={widget.id} style={{ position: 'relative', zIndex: 1 }}>
                <WidgetShell
                  isEditMode={isEditMode}
                  onConfigOpen={() => {
                    setActiveConfigWidget(activeConfigWidgetId === widget.id ? null : widget.id);
                  }}
                  onRemove={() => removeWidget(widget.id)}
                  isDark={isDark}
                  scopeBadge={getWidgetScopeBadge(widget)}
                >
                  {renderWidgetContent(widget)}
                </WidgetShell>
              </div>
            ))}
          </ResponsiveGridLayout>
        )}

        {/* Per-widget config panel */}
        {isEditMode && activeConfigWidget && (
          <WidgetConfigPanel
            widget={activeConfigWidget}
            isDark={isDark}
            onClose={() => setActiveConfigWidget(null)}
            onUpdate={(patch) => updateWidgetConfig(activeConfigWidget.id, patch)}
            scopeMode={scopeMode}
            warehouses={warehouses}
            storageAreasByWarehouse={storageAreasByWarehouse}
            onRemove={() => {
              removeWidget(activeConfigWidget.id);
              setActiveConfigWidget(null);
            }}
          />
        )}

        {/* Add widget floating picker */}
        {isEditMode && showAddPicker && (
          <AddWidgetPicker
            isDark={isDark}
            onAdd={(type) => {
              addWidget(type);
              setShowAddPicker(false);
            }}
            onClose={() => setShowAddPicker(false)}
          />
        )}
      </div>

      {/* ── Edit-mode bottom bar ── */}
      {isEditMode && (
        <div
          className="flex-shrink-0 flex items-center justify-center py-2.5"
          style={{
            borderTop: `1px solid ${isDark ? '#343a43' : '#e5e7eb'}`,
            backgroundColor: isDark ? '#20242b' : '#ffffff',
          }}
        >
          <button
            onClick={() => setShowAddPicker(prev => !prev)}
            className={`inline-flex items-center gap-2 px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
              showAddPicker
                ? 'bg-blue-600 text-white'
                : isDark
                  ? 'bg-[#2b3140] text-gray-200 hover:bg-[#353c4c]'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Plus size={15} />
            Add Widget
          </button>
        </div>
      )}


      {/* ── Reset-to-defaults confirmation ── */}
      {showResetConfirm && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            className="rounded-xl p-5 max-w-xs w-full mx-4 shadow-2xl"
            style={{
              backgroundColor: isDark ? '#20242b' : '#ffffff',
              border: `1px solid ${isDark ? '#343a43' : '#e5e7eb'}`,
            }}
          >
            <h3 className={`text-sm font-semibold mb-1.5 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
              Reset to defaults?
            </h3>
            <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              All widgets and positions will be restored to the original layout. This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowResetConfirm(false)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  isDark ? 'text-gray-300 hover:bg-[#2b3140]' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  resetToDefaults();
                  setShowResetConfirm(false);
                }}
                className="px-3 py-1.5 rounded text-xs font-medium bg-red-600 hover:bg-red-700 text-white transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockDashboard;
